import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
  Label,
  Progress,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  WithToolTip,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { ICompany, ICompanyMetrics } from "@models";
import { getIconUrl } from "@shared";
import { cva } from "class-variance-authority";
import { Building2, Pencil } from "lucide-react";
import type { KeyboardEvent } from "react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { EditCompanyDialog } from "./EditCompanyDialog";

function formatDate(value: string | undefined): string {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

const summaryCardInnerVariants = cva("", {
  variants: {
    type: {
      company:
        "flex flex-col items-center justify-center rounded-lg bg-muted/50 py-6",
      stat: "flex flex-col gap-1",
    },
  },
});

const summaryStatLabelVariants = cva("text-sm font-medium");
const summaryStatValueVariants = cva("mt-2 text-3xl font-bold text-foreground");

const summaryCompanyDetailGridVariants = cva(
  "grid grid-cols-[minmax(140px,auto)_1fr] gap-x-3 gap-y-3 items-center text-sm",
);
const summaryCompanyDetailLabelVariants = cva(
  "font-medium text-muted-foreground",
);
const summaryCompanyDetailValueVariants = cva(
  "min-w-0 truncate text-foreground",
);
const summaryCompanyDetailLogoVariants = cva(
  "flex size-16 items-center justify-center rounded-lg bg-muted text-muted-foreground",
);

interface ISummaryProps {
  company: ICompany | undefined;
  companyUuid: string | undefined;
  metrics: ICompanyMetrics | undefined;
  inactiveUsersCount?: number;
}

export function Summary({
  company,
  companyUuid,
  metrics,
  inactiveUsersCount: inactiveCountOverride,
}: ISummaryProps) {
  const { t } = useTranslation();
  const [companySheetOpen, setCompanySheetOpen] = useState(false);
  const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);

  const handleCompanyCardClick = useCallback(() => {
    if (company) setCompanySheetOpen(true);
  }, [company]);

  const handleCloseCompanySheet = useCallback(() => {
    setCompanySheetOpen(false);
  }, []);

  const companyDetailItems = useMemo(() => {
    if (!company) return [];
    return [
      { label: t("hub.administer.logo"), value: "logo", isLogo: true },
      {
        label: t("hub.administer.company_name"),
        value: company.name ?? "—",
      },
      {
        label: t("hub.administer.skp_customer_id"),
        value: company.skpCustId ?? "—",
      },
      {
        label: t("hub.administer.authentication_method"),
        value: company.authType ?? "—",
      },
      {
        label: t("hub.administer.auth_config_id"),
        value: company.providerName ?? company.protocolType ?? "—",
      },
      {
        label: t("hub.locale_switch.menu_label"),
        value: company.language ?? "—",
      },
      {
        label: t("hub.administer.street_address"),
        value: company.serviceStreetAddress ?? "—",
      },
      {
        label: t("hub.administer.city"),
        value: company.serviceCity ?? "—",
      },
      {
        label: t("hub.administer.state"),
        value: company.serviceState ?? "—",
      },
      {
        label: t("hub.administer.country"),
        value: company.serviceCountry ?? "—",
      },
      {
        label: t("hub.administer.zip_code"),
        value: company.servicePostalCode ?? "—",
      },
      {
        label: t("hub.administer.created"),
        value: formatDate(company.createdAt),
      },
    ];
  }, [company, t]);

  return (
    <div className="grid max-w-5xl gap-4 sm:grid-cols-3 mb-5">
      <Card
        role="button"
        tabIndex={0}
        onClick={handleCompanyCardClick}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) =>
          (e.key === "Enter" || e.key === " ") && handleCompanyCardClick()
        }
        className={cn(
          company && "cursor-pointer transition-colors hover:bg-muted/30",
        )}>
        <CardHeader className="flex flex-row items-start justify-between gap-4 min-w-0 pt-2">
          <CardTitle className="font-medium flex-1 min-w-0 pt-3">
            <WithToolTip tooltipText={company?.name}>
              <span className="block truncate text-left w-full cursor-default">
                {company?.name}
              </span>
            </WithToolTip>
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            aria-hidden
            onClick={(e) => {
              e.stopPropagation();
              setEditCompanyDialogOpen(true);
            }}>
            <Pencil className="size-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className={cn(summaryCardInnerVariants({ type: "company" }))}>
            {company?.logo ? (
              <Avatar>
                <AvatarImage
                  src={getIconUrl(company.logo)}
                  className="object-contain"
                />
                <AvatarFallback>
                  <Building2 className="size-8 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
            ) : (
              <Building2 className="size-8 text-muted-foreground" />
            )}
            <CardTitle className="font-medium w-full text-center px-4 min-w-0 mt-2">
              <WithToolTip tooltipText={company?.name}>
                <span className="block truncate text-center w-full cursor-default">
                  {company?.name}
                </span>
              </WithToolTip>
            </CardTitle>
          </div>
        </CardContent>
      </Card>
      <Card className="flex flex-col overflow-hidden">
        <CardHeader className="pb-0">
          <CardTitle className="text-lg font-bold">
            {t("hub.administer.total_users")} ({metrics?.usersCount ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col flex-1 pt-4 pb-0">
          <Progress
            value={
              (metrics?.usersCount ?? 0) > 0
                ? ((metrics?.activeUsersCount ?? 0) /
                    (metrics?.usersCount ?? 0)) *
                  100
                : 0
            }
            className="h-1.5"
          />
          <div className="flex justify-between items-center mt-2 text-xs text-muted-foreground">
            <Label>
              {t("hub.profile.tenant_active")} {metrics?.activeUsersCount ?? 0}
            </Label>
            <Label>
              {t("hub.profile.tenant_inactive")}{" "}
              {inactiveCountOverride ?? metrics?.inactiveUsersCount ?? 0}
            </Label>
          </div>
        </CardContent>
        <CardFooter className="mt-auto flex-col items-start bg-muted/30 px-6 py-4 border-0">
          <Label className="text-sm text-muted-foreground">
            {t("hub.profile.tenant_active")}/ {t("hub.profile.tenant_inactive")}{" "}
            Users
          </Label>
          <Label className="font-bold text-lg mt-1">
            {metrics?.activeUsersCount ?? 0} /{" "}
            {inactiveCountOverride ?? metrics?.inactiveUsersCount ?? 0}
          </Label>
        </CardFooter>
      </Card>
      <Card>
        <CardContent>
          <CardDescription className={cn(summaryStatLabelVariants())}>
            {t("hub.administer.applications")}
          </CardDescription>
          <CardTitle className={cn(summaryStatValueVariants())}>
            {metrics?.applicationCount ?? 0}
          </CardTitle>
        </CardContent>
      </Card>

      <Sheet
        open={companySheetOpen}
        onOpenChange={(open: boolean) => !open && handleCloseCompanySheet()}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-lg font-bold text-primary">
              {t("hub.administer.company_details")}
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="min-h-0 flex-1" viewportClassName="py-4">
            <div className={cn(summaryCompanyDetailGridVariants())}>
              {companyDetailItems.map(({ label, value, isLogo }) => (
                <Fragment key={label}>
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <Label
                      className={cn(
                        summaryCompanyDetailLabelVariants(),
                        "shrink-0 text-left",
                      )}>
                      {label}
                    </Label>
                    <Label
                      className={cn(
                        summaryCompanyDetailLabelVariants(),
                        "shrink-0 text-right",
                      )}>
                      :
                    </Label>
                  </div>
                  <div className={cn(summaryCompanyDetailValueVariants())}>
                    {isLogo ? (
                      <div className={cn(summaryCompanyDetailLogoVariants())}>
                        {company?.logo ? (
                          <Avatar>
                            <AvatarImage
                              src={getIconUrl(company.logo)}
                              className="object-contain"
                            />
                            <AvatarFallback>
                              <Building2 className="size-8 text-muted-foreground" />
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <Building2 className="size-8 text-muted-foreground" />
                        )}
                      </div>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Label className="block min-w-0 cursor-default truncate font-normal">
                            {value}
                          </Label>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="wrap-break-word">{value}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </ScrollArea>
          <SheetFooter className="flex-row justify-start border-t pt-4">
            <Button variant="outline" onClick={handleCloseCompanySheet}>
              {t("hub.administer.cancel")}
            </Button>
            <Button onClick={() => setEditCompanyDialogOpen(true)}>
              {t("hub.administer.edit")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <EditCompanyDialog
        open={editCompanyDialogOpen}
        onOpenChange={setEditCompanyDialogOpen}
        company={company}
        companyUuid={companyUuid}
      />
    </div>
  );
}
